/**
 * 「催一下」：空闲条右端，紧跟在「等待 老周 行动」后面，读起来就是「催老周」。
 *
 * 位置常驻：没到时间用 visibility:hidden 占着 —— 亮起时不会把别的东西挤到手指底下。
 * 只挂一个定时器指向下一次状态变化（能催 / 进入最后几秒），不做倒计时（红线 #48）。
 */
import { useEffect, useReducer } from 'react'
import type { ReactNode } from 'react'
import type { PlayerView } from '@poker/shared'

import { fireHaptic } from '../fx/haptics.js'
import { useRoomLink } from '../room/roomLink.js'
import { useSettingsStore } from '../store/index.js'
import { selectNetView, useNetStore } from '../transport/applyMessage.js'
import { nextNudgeChangeAt, nudgeState } from './talkDerive.js'
import { useTalkStore } from './talkStore.js'

export interface NudgeButtonProps {
  /** 覆盖 store 里的值（回放 / 单测用；省略就订阅 store） */
  readonly view?: PlayerView | null
  readonly online?: boolean
  readonly nudgedKey?: string | null
}

export function NudgeButton(over: NudgeButtonProps = {}): ReactNode {
  const storeView = useNetStore(selectNetView)
  const storeOnline = useNetStore((s) => s.phase === 'live')
  const storeNudged = useTalkStore((s) => s.nudgedKey)
  const view = over.view === undefined ? storeView : over.view
  const online = over.online ?? storeOnline
  const nudgedKey = over.nudgedKey === undefined ? storeNudged : over.nudgedKey
  const link = useRoomLink()
  const setNudged = useTalkStore((s) => s.setNudged)
  const haptics = useSettingsStore((s) => s.haptics)
  const [, tick] = useReducer((x: number) => x + 1, 0)

  const serverNow = link.clock.now()
  const st = nudgeState(view, serverNow, nudgedKey)
  const changeAt = nextNudgeChangeAt(st, serverNow)

  useEffect(() => {
    if (changeAt === null) return undefined
    const t = setTimeout(tick, Math.max(0, link.clock.msUntil(changeAt)) + 20)
    return () => clearTimeout(t)
  }, [changeAt, link])

  if (st.kind === 'none') return null
  const shown = st.kind === 'ready' || st.kind === 'done'
  const ready = st.kind === 'ready' && online

  return (
    <button
      type="button"
      className="talk-nudge pressable"
      data-state={st.kind}
      style={shown ? undefined : { visibility: 'hidden' }}
      aria-hidden={!shown}
      tabIndex={shown ? 0 : -1}
      disabled={!ready}
      aria-label={st.kind === 'done' ? `已催过 ${st.name}` : `催 ${st.name} 出牌`}
      onClick={(e) => {
        if (!ready) return
        link.send({ type: 'emote', emote: 'nudge', targetSeat: st.seat })
        setNudged(st.key)
        fireHaptic('select', haptics, e.currentTarget)
      }}
    >
      {st.kind === 'done' ? '已催' : '催一下'}
    </button>
  )
}
